import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Tag, 
  Space, 
  message, 
  Tooltip,
  Row,
  Col,
  Statistic,
  Progress,
  Timeline,
  Avatar,
  Divider
} from 'antd';
import { 
  EditOutlined, 
  EyeOutlined, 
  CommentOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ProjectOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const MyTasks = ({ currentUser }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskStats, setTaskStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();

  useEffect(() => {
    fetchMyTasks();
    fetchTaskStats();
  }, []);

  const fetchMyTasks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/user/${currentUser.id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const response_data = await response.json();
      
      // Handle wrapped response format
      const data = response_data.data || response_data;
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Tasks data is not an array:', data);
        setTasks([]);
        return;
      }
      
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      message.error('Failed to fetch tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/stats/overview?userId=${currentUser.id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setTaskStats(data || {});
    } catch (error) {
      console.error('Error fetching task stats:', error);
      setTaskStats({});
    }
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        message.success('Task status updated successfully');
        fetchMyTasks();
        fetchTaskStats();
      } else {
        const error = await response.json();
        message.error(error.message || 'Failed to update task status');
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      message.error('Failed to update task status');
    }
  };

  const handleViewDetails = async (task) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/${task._id}`);
      const data = await response.json();
      setSelectedTask(data);
      setDetailModalVisible(true);
    } catch (error) {
      console.error('Error fetching task details:', error);
      message.error('Failed to fetch task details');
    }
  };

  const handleAddComment = (task) => {
    setSelectedTask(task);
    commentForm.resetFields();
    setCommentModalVisible(true);
  };

  const handleSubmitComment = async (values) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/${selectedTask._id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.id,
          comment: values.comment
        }),
      });

      if (response.ok) {
        message.success('Comment added successfully');
        setCommentModalVisible(false);
        fetchMyTasks();
        // Refresh task details if modal is open
        if (detailModalVisible && selectedTask) {
          handleViewDetails(selectedTask);
        }
      } else {
        const error = await response.json();
        message.error(error.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      message.error('Failed to add comment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'orange',
      'in-progress': 'blue',
      'completed': 'green',
      'cancelled': 'red'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'green',
      'medium': 'orange',
      'high': 'red',
      'urgent': 'purple'
    };
    return colors[priority] || 'default';
  };

  const getStatusActions = (task) => {
    const actions = [];
    
    if (task.status === 'pending') {
      actions.push(
        <Button
          key="start"
          type="primary"
          size="small"
          icon={<PlayCircleOutlined />}
          onClick={() => handleStatusUpdate(task._id, 'in-progress')}
        >
          Start
        </Button>
      );
    }
    
    if (task.status === 'in-progress') {
      actions.push(
        <Button
          key="complete"
          type="primary"
          size="small"
          icon={<CheckCircleOutlined />}
          onClick={() => handleStatusUpdate(task._id, 'completed')}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Complete
        </Button>
      );
    }
    
    return actions;
  };

  const filteredTasks = tasks.filter(task => {
    if (statusFilter === 'all') return true;
    return task.status === statusFilter;
  });

  const columns = [
    {
      title: 'Task',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <ProjectOutlined style={{ marginRight: '4px' }} />
            {record.projectName}
          </div>
          {record.tags && record.tags.length > 0 && (
            <div style={{ marginTop: '4px' }}>
              {record.tags.map(tag => (
                <Tag key={tag} size="small">{tag}</Tag>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {priority.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date, record) => {
        if (!date) return '-';
        const dueDate = dayjs(date);
        const isOverdue = dueDate.isBefore(dayjs()) && record.status !== 'completed';
        return (
          <span style={{ color: isOverdue ? '#ff4d4f' : 'inherit' }}>
            {dueDate.format('MMM DD, YYYY')}
            {isOverdue && <ClockCircleOutlined style={{ marginLeft: '4px', color: '#ff4d4f' }} />}
          </span>
        );
      },
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        if (record.estimatedHours === 0) return '-';
        const progress = Math.min((record.actualHours / record.estimatedHours) * 100, 100);
        return (
          <div>
            <Progress 
              percent={Math.round(progress)} 
              size="small" 
              status={progress > 100 ? 'exception' : 'normal'}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {record.actualHours}h / {record.estimatedHours}h
            </div>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Space>
            <Tooltip title="View Details">
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                onClick={() => handleViewDetails(record)}
              />
            </Tooltip>
            <Tooltip title="Add Comment">
              <Button 
                type="text" 
                icon={<CommentOutlined />} 
                onClick={() => handleAddComment(record)}
              />
            </Tooltip>
          </Space>
          <Space>
            {getStatusActions(record)}
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="My Tasks"
              value={taskStats.totalTasks || 0}
              prefix={<ProjectOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending"
              value={taskStats.pendingTasks || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="In Progress"
              value={taskStats.inProgressTasks || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Completed"
              value={taskStats.completedTasks || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="My Tasks"
        extra={
          <Select
            style={{ width: 150 }}
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <Option value="all">All Tasks</Option>
            <Option value="pending">Pending</Option>
            <Option value="in-progress">In Progress</Option>
            <Option value="completed">Completed</Option>
          </Select>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredTasks}
          rowKey="_id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} tasks`,
          }}
        />
      </Card>

      {/* Task Details Modal */}
      <Modal
        title="Task Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTask && (
          <div>
            <Row gutter={16}>
              <Col span={16}>
                <h3>{selectedTask.title}</h3>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  {selectedTask.description}
                </p>
                
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                  <Col span={8}>
                    <strong>Project:</strong><br />
                    {selectedTask.projectName}
                  </Col>
                  <Col span={8}>
                    <strong>Created By:</strong><br />
                    {selectedTask.createdByName}
                  </Col>
                  <Col span={8}>
                    <strong>Due Date:</strong><br />
                    {selectedTask.dueDate ? dayjs(selectedTask.dueDate).format('MMM DD, YYYY') : 'Not set'}
                  </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: '16px' }}>
                  <Col span={8}>
                    <strong>Status:</strong><br />
                    <Tag color={getStatusColor(selectedTask.status)}>
                      {selectedTask.status.toUpperCase()}
                    </Tag>
                  </Col>
                  <Col span={8}>
                    <strong>Priority:</strong><br />
                    <Tag color={getPriorityColor(selectedTask.priority)}>
                      {selectedTask.priority.toUpperCase()}
                    </Tag>
                  </Col>
                  <Col span={8}>
                    <strong>Estimated Hours:</strong><br />
                    {selectedTask.estimatedHours || 0}h
                  </Col>
                </Row>

                {selectedTask.tags && selectedTask.tags.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong>Tags:</strong><br />
                    {selectedTask.tags.map(tag => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </div>
                )}
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  {getStatusActions(selectedTask)}
                </div>
              </Col>
            </Row>

            {selectedTask.comments && selectedTask.comments.length > 0 && (
              <>
                <Divider>Comments</Divider>
                <Timeline>
                  {selectedTask.comments.map((comment, index) => (
                    <Timeline.Item key={index}>
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: '8px' }} />
                          {comment.userName}
                          <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                            {dayjs(comment.createdAt).format('MMM DD, YYYY HH:mm')}
                          </span>
                        </div>
                        <div>{comment.comment}</div>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Add Comment Modal */}
      <Modal
        title="Add Comment"
        open={commentModalVisible}
        onCancel={() => setCommentModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={commentForm}
          layout="vertical"
          onFinish={handleSubmitComment}
        >
          <Form.Item
            name="comment"
            label="Comment"
            rules={[{ required: true, message: 'Please enter a comment' }]}
          >
            <TextArea rows={4} placeholder="Enter your comment..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCommentModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Comment
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MyTasks;
